/*
  VF printing guard, global.

  Why this exists.

  snippets/vf-print-cart-note.liquid already checks for a broken print order,
  but it is only rendered on the cart PAGE. The store runs a cart drawer
  (settings.cart_type == 'drawer'), and snippets/cart-drawer.liquid never
  renders it. So a customer could open the drawer, delete the Logo Printing
  fee line, and hit the drawer's own checkout button without the guard ever
  loading. That is what happened on order #21937: the garment carried a valid
  print reference and uploaded artwork, and the GBP 15 fee line was gone.

  Rather than patch the drawer template and leave the next surface uncovered,
  this runs on every page from snippets/meta-tags.liquid, reads the live cart
  from /cart.js, and blocks every checkout route it can reach.

  What counts as broken:

    - a printed garment with no _vf_print_ref, or with no uploaded artwork
      anywhere in the cart. The file only rides on the first garment line, so
      the artwork check is cart-wide, not per line.
    - printed garments with zero fee units.
    - fewer fee units than printed units, which means some garments would be
      printed unpaid. The old cart-page guard only warned about this. It
      blocks now, because the money is the same money.
    - a fee line with no printed garment left, which would charge for nothing.

  More fee units than printed units is a warning, not a block. The customer
  has overpaid, which is recoverable, and stopping their checkout is not.

  A determined customer can still type /checkout straight into the address
  bar. Nothing in the theme layer can stop that. If that ever shows up in the
  orders, the fix is a cart checkout validation function, which needs an app.

  4 Oct 2026, crash fix. apply() removes and re-inserts the guard note, and
  the MutationObserver below was watching the body for exactly that kind of
  change. So whenever a basket was blocked or warned, every apply() triggered
  another apply(), forever, on every page (this file loads site-wide). Phones
  ran out of memory and Safari showed "A problem repeatedly occurred". The
  observer is now disconnected while apply() edits the page and reconnected
  afterwards, so it only ever reacts to changes made by Dawn or apps.

  18 Oct 2026, name fee fix. The name or trade line (VF-PRINT-NAME) is its own
  per-garment charge, not a print fee, but it matched the VF-PRINT prefix and
  was being added to the fee count. Any basket with a name therefore showed
  double the fee units and the "paying for printing twice" warning. Name
  lines are now counted separately and only checked for being orphaned. The
  new Back only option (VF-PRINT-BACK) is a normal print fee and needs nothing
  here.
*/

(function () {
  if (window.vfPrintGuardLoaded) return;
  window.vfPrintGuardLoaded = true;

  var FEE_SKU = 'VF-PRINT';
  var NAME_SKU = 'VF-PRINT-NAME';
  var CART_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  var state = { block: false, warn: false, reason: '', printed: 0, fee: 0 };
  var refreshing = false;
  var observer = null;

  /* ---------- reading the cart ---------- */

  function isFeeLine(item) {
    return String(item.sku || '').indexOf(FEE_SKU) > -1;
  }

  function isNameLine(item) {
    return String(item.sku || '').indexOf(NAME_SKU) > -1;
  }

  function evaluate(cart) {
    var printed = 0;
    var fee = 0;
    var nameFee = 0;
    var missingRef = false;
    var hasLogo = false;

    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};

      if (isNameLine(item)) {
        nameFee += item.quantity;
        return;
      }

      if (isFeeLine(item)) {
        fee += item.quantity;
        return;
      }

      if (!props['Print option']) return;

      printed += item.quantity;
      if (!props['_vf_print_ref']) missingRef = true;

      var logo = props['Logo'];
      if (typeof logo === 'string' && logo.indexOf('/uploads/') > -1) hasLogo = true;
    });

    state.printed = printed;
    state.fee = fee;
    state.block = false;
    state.warn = false;
    state.reason = '';

    if (printed > 0) {
      if (missingRef || !hasLogo) {
        state.block = true;
        state.reason =
          'Your logo did not upload correctly, so we have nothing to print from. Remove the printed item, then add it again from the product page with your logo attached.';
      } else if (fee === 0) {
        state.block = true;
        state.reason =
          'The printing charge is missing from your basket, so the printing has not been paid for. Remove the printed item, then add it again from the product page.';
      } else if (fee < printed) {
        state.block = true;
        state.reason =
          'You have ' +
          printed +
          ' item(s) to be printed but printing is only paid for on ' +
          fee +
          '. Remove the printed item and add it again with the quantity you want.';
      } else if (fee > printed) {
        state.warn = true;
        state.reason =
          'You are paying for printing on ' +
          fee +
          ' item(s) but only ' +
          printed +
          ' will be printed. Adjust the quantity so you are not charged twice.';
      }
    } else if (fee > 0 || nameFee > 0) {
      state.block = true;
      state.reason =
        'There is a printing charge in your basket with nothing to print. Remove it, or add the garment you wanted printing on.';
    }
  }

  function refresh() {
    if (refreshing) return;
    refreshing = true;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

    nativeFetch
      .call(window, root + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        evaluate(cart);
        apply();
      })
      .catch(function () {
        // A failed read must not lock a customer out of a healthy basket.
        state.block = false;
        state.warn = false;
        apply();
      })
      .then(function () {
        refreshing = false;
      });
  }

  /* ---------- applying it to the page ---------- */

  function ensureStyles() {
    if (document.getElementById('VfPrintGuardStyles')) return;
    var style = document.createElement('style');
    style.id = 'VfPrintGuardStyles';
    style.textContent = [
      '.vf-print-guard-note{margin:0 0 1.2rem;padding:1.2rem 1.4rem;font-size:1.4rem;line-height:1.45;border-left:.3rem solid #c0392b;background:rgba(192,57,43,.08);border-radius:var(--inputs-radius,.4rem);text-align:left}',
      '.vf-print-guard-note--warn{border-left-color:#b8860b;background:rgba(184,134,11,.08)}',
      '.vf-print-guard-note strong{display:block;margin-bottom:.4rem}',
      '.vf-print-blocked [name="checkout"],.vf-print-blocked .cart__dynamic-checkout-buttons,.vf-print-blocked .additional-checkout-buttons{opacity:.4;pointer-events:none}',
    ].join('');
    document.head.appendChild(style);
  }

  function addNote(target) {
    var note = document.createElement('div');
    note.className = 'vf-print-guard-note' + (state.block ? '' : ' vf-print-guard-note--warn');
    note.setAttribute('data-vf-print-guard-note', '');
    note.setAttribute('role', 'alert');

    var heading = document.createElement('strong');
    heading.textContent = state.block ? 'We cannot take this order yet' : 'Check your printing quantity';

    note.appendChild(heading);
    note.appendChild(document.createTextNode(state.reason));
    target.parentNode.insertBefore(note, target);
  }

  function observe() {
    if (observer && document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function apply() {
    // Stop watching while we edit the page, otherwise our own note changes
    // trigger the observer, which calls apply() again, in an endless loop.
    if (observer) observer.disconnect();

    try {
      ensureStyles();

      document.documentElement.classList.toggle('vf-print-blocked', state.block);

      document.querySelectorAll('[name="checkout"]').forEach(function (el) {
        if (state.block) {
          el.setAttribute('disabled', 'disabled');
          el.setAttribute('aria-disabled', 'true');
        } else if (el.getAttribute('aria-disabled') === 'true') {
          // Only re-enable what this guard disabled, so an empty basket stays
          // disabled the way Dawn left it.
          el.removeAttribute('disabled');
          el.removeAttribute('aria-disabled');
        }
      });

      document.querySelectorAll('[data-vf-print-guard-note]').forEach(function (el) {
        el.remove();
      });

      if (!state.block && !state.warn) return;

      // Both the cart page and the drawer wrap their checkout button in
      // .cart__ctas, so one selector covers both surfaces.
      document.querySelectorAll('.cart__ctas').forEach(addNote);
    } finally {
      observe();
    }
  }

  /* ---------- stopping the checkout ---------- */

  document.addEventListener(
    'click',
    function (evt) {
      if (!state.block) return;

      var target = evt.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest(
        '[name="checkout"], .cart__dynamic-checkout-buttons button, .additional-checkout-buttons button'
      );
      if (!button) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      var note = document.querySelector('[data-vf-print-guard-note]');
      if (note) note.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    true
  );

  document.addEventListener(
    'submit',
    function (evt) {
      if (!state.block) return;

      var form = evt.target;
      if (!form || !form.action) return;
      if (form.action.indexOf('/cart') === -1 && form.action.indexOf('/checkout') === -1) return;

      // Quantity and note updates post to the same form, so only a genuine
      // checkout submit is stopped.
      var submitter = evt.submitter;
      if (submitter && submitter.name !== 'checkout') return;

      evt.preventDefault();
      evt.stopImmediatePropagation();
    },
    true
  );

  /* ---------- staying in sync ---------- */

  function touchesCart(url) {
    var value = String(url || '');
    return CART_PATHS.some(function (path) {
      return value.indexOf(path) > -1;
    });
  }

  // Captured before patching, so refresh() never re-enters our own wrapper and
  // never trips the printing net guard on the product page.
  var nativeFetch = window.fetch;

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : input && input.url;
      var result = nativeFetch.apply(this, arguments);
      if (touchesCart(url) && result && typeof result.then === 'function') {
        result.then(
          function () {
            refresh();
          },
          function () {}
        );
      }
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (touchesCart(url)) {
      this.addEventListener('loadend', function () {
        refresh();
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  // Dawn re-renders the drawer and the cart section with innerHTML, which
  // wipes the injected note and restores the checkout button. Re-applying on
  // those mutations costs nothing because it reads no network, and apply()
  // disconnects the observer while it works so it never reacts to itself.
  function start() {
    observer = new MutationObserver(function () {
      apply();
    });
    apply();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', refresh);
})();
